import type { ChangeEvent, DragEvent, FC, MouseEvent } from 'react';
import React, { useRef, useState } from 'react';
import { Controller, useFormContext } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { Button } from '@components/atoms/button';
import { Icon } from '@components/atoms/icon';
import { UploadIcon } from '@components/icons';
import { DeleteIcon } from '@components/icons/delete-icon';
import { LabeledField } from '@components/moleculs/labeled-field';
import { TextField } from '@components/moleculs/text-field';
import { Container } from '@components/organisms/forms/form-upload-field/styles';
import type { FormUploadFieldProps } from '@components/organisms/forms/form-upload-field/types';
import { getFileExtensionsFromMimeType } from '@components/organisms/forms/form-upload-field/utils/get-file-extensions-from-mime-type';
import { useFileSize } from '@modules/shared/hooks/use-file-size';
import cn from 'classnames';

export const FormUploadField: FC<FormUploadFieldProps> = ({
    name,
    textFieldProps,
    className,
    labelProps,
    isRequired,
    uploadProps,
}) => {
    const { t } = useTranslation();
    const { control } = useFormContext();
    const { getSize } = useFileSize();
    const inputRef = useRef<HTMLInputElement | null>(null);
    const [isOnDragOver, setIsOnDragOver] = useState(false);

    const handleUploadClick = (e: MouseEvent) => {
        e.preventDefault();
        e.stopPropagation();
        inputRef.current?.click();
    };

    const handleDragOver = (event: DragEvent) => {
        event.preventDefault();
        event.stopPropagation();
        setIsOnDragOver(true);
    };

    const handleDragLeave = () => {
        setIsOnDragOver(false);
    };

    const allowedExtensions = uploadProps?.mimeTypes?.reduce((acc, type) => {
        const extensions = getFileExtensionsFromMimeType(type);

        acc.push(...extensions);

        return acc;
    }, [] as string[]);

    const element = (
        <Controller
            name={name}
            control={control}
            render={({ field, fieldState }) => {
                const handleDrop = (event: DragEvent) => {
                    event.preventDefault();
                    event.stopPropagation();

                    const file = event.dataTransfer.files[0];

                    field.onChange(file);
                    setIsOnDragOver(false);
                };

                const handleFileChange = (e: ChangeEvent<HTMLInputElement>) => {
                    const file = e.target.files?.[0];

                    if (!file) {
                        return;
                    }

                    field.onChange(e.target.files?.[0]);
                };

                const handleDeleteClick = () => {
                    field.onChange(null);
                };

                const showHint = !!allowedExtensions?.length || !!uploadProps?.maxSize;

                return (
                    <Container>
                        <div className='input-container'>
                            <TextField
                                {...textFieldProps}
                                value={
                                    field.value?.name
                                        ? `${field.value.name} (${getSize(field.value.size, 1)})`
                                        : t('common.uploadFile')
                                }
                                helperText={
                                    <React.Fragment>
                                        {!!fieldState.error?.message && (
                                            <span>{fieldState.error?.message}</span>
                                        )}
                                        {showHint && (
                                            <React.Fragment>
                                                {!!allowedExtensions?.length && (
                                                    <React.Fragment>
                                                        <span className='hint'>
                                                            {t('common.formats', {
                                                                formats:
                                                                    allowedExtensions.join(', '),
                                                            })}
                                                            .
                                                        </span>
                                                    </React.Fragment>
                                                )}
                                                {!!uploadProps?.maxSize && (
                                                    <span className='hint'>
                                                        {t('common.maxSize', {
                                                            size: getSize(uploadProps?.maxSize, 0),
                                                        })}
                                                    </span>
                                                )}
                                            </React.Fragment>
                                        )}
                                    </React.Fragment>
                                }
                                error={!!fieldState.error?.message}
                                InputProps={{
                                    ...textFieldProps?.InputProps,
                                    disabled: true,
                                    startAdornment: <UploadIcon className='upload-icon' />,
                                    ...(field.value
                                        ? {
                                              endAdornment: (
                                                  <Icon
                                                      icon={<DeleteIcon />}
                                                      $clickable={true}
                                                      onClick={handleDeleteClick}
                                                      className='delete-icon'
                                                  />
                                              ),
                                          }
                                        : {}),
                                }}
                                onDrop={handleDrop}
                                onDragOver={handleDragOver}
                                onDragLeave={handleDragLeave}
                                className={cn({ 'drag-over': isOnDragOver })}
                            />
                        </div>
                        <input
                            className='file-input'
                            ref={inputRef}
                            value={field.value ? '' : undefined}
                            type='file'
                            accept={uploadProps?.mimeTypes?.join(',')}
                            onChange={handleFileChange}
                        />
                        {!uploadProps?.isHideUploadButton && (
                            <Button variant='secondary' size='xl' onClick={handleUploadClick}>
                                {t('common.upload')}
                            </Button>
                        )}
                    </Container>
                );
            }}
        />
    );

    if (labelProps?.label) {
        return (
            <LabeledField
                {...labelProps}
                isRequired={isRequired || labelProps?.isRequired}
                className={className}
            >
                {element}
            </LabeledField>
        );
    }

    return element;
};
